import React, { useState } from "react";
import { Fingerprint, Clock, CalendarDays, FileText, BarChart3, UserCheck, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/HeaderFooter/Footer";
import { useTheme } from "../../Context/themeContext";

const HomePage = () => {

    const [isProfileOpen, setIsProfileOpen] = useState(false);// To check
    const navigate = useNavigate()
    const { theme } = useTheme();

    const isDark = theme === 'dark';
    const bgPrimary = isDark ? 'bg-[#0B0F1C]' : 'bg-gray-50';
    const bgGradient = isDark ? 'bg-gradient-to-b from-[#0B0F1C] to-[#070A12]' : 'bg-gradient-to-b from-gray-50 to-gray-100';
    const borderColor = isDark ? 'border-white/10' : 'border-gray-200';
    const borderHover = isDark ? 'hover:border-blue-500/50' : 'hover:border-blue-400';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-blue-200/70' : 'text-gray-600';
    const textMuted = isDark ? 'text-blue-200/60' : 'text-gray-500';
    const bgGlassHover = isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200/80';
    const orbColor1 = isDark ? 'bg-blue-600/10' : 'bg-blue-400/20';
    const orbColor2 = isDark ? 'bg-cyan-600/10' : 'bg-cyan-400/20';
    const bgGlass = isDark ? 'bg-white/5' : 'bg-gray-100/80';

    // Attendance Management Functionalities
    const functionalities = [
        {
            icon: Clock,
            title: "Real-time Tracking",
            description:
                "Live Check-In/Out with timestamp accuracy. Monitor staff attendance as it happens from any device, anywhere in the world.",
        },
        {
            icon: CalendarDays,
            title: "Shift Scheduling",
            description:
                "Easy schedule with confidence. Day and Night Schedule available in all Timezones with Multiple Check-In and Check-Out",
        },
        {
            icon: FileText,
            title: "Attendance Export",
            description:
                "Auto-calculate work hours, overtime, and undertime. Export to Excel and API connection to any payroll software.",
        },
        {
            icon: BarChart3,
            title: "Advanced Analytics",
            description:
                "Attendance patterns, tardiness trends, and department-wise reports. Visual dashboards with date filter capability.",
        },
        {
            icon: UserCheck,
            title: "Leave Management",
            description:
                "Digital leave requests, approvals, and balance tracking. Leave tracking and leave analysis.",
        },
        {
            icon: Bell,
            title: "Smart Alerts",
            description:
                "Real-time notifications for late arrivals, early departures, and absentees.",
        }
    ];

    // Close popup when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (isProfileOpen && !event.target.closest('.profile-container')) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isProfileOpen]);

    /*useEffect(() => {
      const timer = setTimeout(() => {
        setloading(false);
           }, 1000);
      return () => clearTimeout(timer); // Fixes crash if component unmounts before 1s
    }, []); */

    function handlerClick(navigation) {
        if (navigation === 'Product') navigate('/product')
        if (navigation === 'Contact') navigate('/contact')
        if (navigation === 'watch-demo') navigate('/watch-demo')
    }

    return (
        <div className={`font-sans ${bgPrimary} ${textPrimary} overflow-x-hidden transition-colors duration-300`}>
            {/* HERO Section */}
            <section className={`relative px-6 pb-24 overflow-hidden pt-36 ${bgPrimary} transition-colors duration-300`}>
                <div className={`absolute top-40 left-20 w-[500px] h-[500px] ${orbColor1} rounded-full blur-[120px] animate-pulse-slow`}></div>
                <div className={`absolute bottom-20 right-20 w-[600px] h-[600px] ${orbColor2} rounded-full blur-[120px] animate-pulse-slow animation-delay-2000`}></div>


                <div className="container relative z-10 mx-auto">
                    <div className="flex flex-col items-center gap-16 lg:flex-row">
                        {/* Left Content */}
                        <div className="flex-1 text-center lg:text-left">
                            <div className={`inline-flex items-center gap-2 px-4 py-2 mb-8 border rounded-full ${bgGlass} ${borderColor} backdrop-blur-sm`}>
                                <span className="relative flex w-2 h-2">
                                    <span className="absolute inline-flex w-full h-full bg-green-400 rounded-full opacity-75 animate-ping"></span>
                                    <span className="relative inline-flex w-2 h-2 bg-green-500 rounded-full"></span>
                                </span>
                                <span className={`text-xs font-medium ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>Trusted by 20+ Organizations</span>
                            </div>

                            <h1 className="text-5xl font-bold leading-tight tracking-tight md:text-6xl lg:text-7xl">
                                Smart Tracking,
                                <br />
                                <span className="relative pb-4 text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-300 bg-clip-text">
                                    Zero Hassle
                                    <svg className="absolute left-0 w-full -bottom-2" viewBox="0 0 100 10" preserveAspectRatio="none">
                                        <path d="M0,5 Q25,0 50,5 T100,5" stroke="url(#gradient)" strokeWidth="2" fill="none" />
                                        <defs>
                                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" style={{ stopColor: '#60A5FA', stopOpacity: 1 }} />
                                                <stop offset="100%" style={{ stopColor: '#22D3EE', stopOpacity: 1 }} />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </span>
                            </h1>

                            <p className={`max-w-2xl mx-auto mt-6 text-lg leading-relaxed md:text-xl ${textSecondary} lg:mx-0`}>
                                The complete ecosystem that saves time{' '}
                                <span className={`px-2 py-1 font-semibold rounded-lg ${isDark ? 'text-white bg-white/5' : 'text-gray-900 bg-gray-200'}`}>8+ hours weekly</span>.
                                Finger scanning with hardware, and tracking with smart analytics.
                            </p>

                            <div className="flex flex-wrap justify-center gap-4 mt-10 lg:justify-start">
                                <a href="https://pages.razorpay.com/CICO-Time" className="relative px-8 py-4 font-semibold text-white transition-all duration-300 transform group bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl hover:shadow-2xl hover:shadow-blue-600/30 hover:scale-105">
                                    <span className="relative z-10 flex items-center gap-2">
                                        <i className="transition-transform duration-300 fas fa-rocket group-hover:rotate-12"></i>
                                        Buy Now
                                    </span>
                                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl blur-xl group-hover:opacity-50"></div>
                                </a>
                                <button onClick={() => { handlerClick('watch-demo') }} className={`flex items-center gap-2 px-8 py-4 font-semibold transition-all duration-300 border ${bgGlass} ${bgGlassHover} ${borderColor} rounded-xl backdrop-blur-sm hover:border-white/20 group`}>
                                    <i className={`transition-transform duration-300 ${isDark ? 'text-blue-400' : 'text-blue-600'} fas fa-play-circle group-hover:scale-110`}></i>
                                    Watch Demo
                                </button>
                                <a
                                    href="/downloads/app-arm64-v8a-release 35.apk"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download="CICO-Time.apk"
                                    className="app-download-btn group"
                                    >
                                    <i className="fas fa-download icon"></i>
                                    <span>Download CICO-Time App</span>
                                </a>
                            </div>
                        </div>

                        {/* Right Content - Modern 3D Card */}
                        <div className="flex justify-center flex-1">
                            <div className="relative w-[450px] h-[500px] lg:w-[550px] lg:h-[600px] group">
                                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20' : 'bg-gradient-to-r from-blue-400/20 to-cyan-400/20'} rounded-3xl blur-3xl animate-pulse-slow`}></div>

                                <div className="relative w-full h-full transition-all duration-700 transform-gpu group-hover:scale-105 group-hover:-translate-y-4">
                                    <div className="absolute inset-0 transition-all duration-700 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-3xl rotate-6 transform-gpu opacity-20 blur-2xl group-hover:rotate-12"></div>

                                    <div className={`relative w-full h-full ${isDark ? 'bg-gradient-to-br from-[#1A1F2E] to-[#0B0F1C]' : 'bg-gradient-to-br from-white to-gray-100'} rounded-3xl border ${borderColor} overflow-hidden shadow-2xl backdrop-blur-xl`}>
                                        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10' : 'bg-gradient-to-br from-blue-400/5 via-transparent to-cyan-400/5'}`}></div>

                                        <div className="relative flex flex-col items-center justify-center h-full p-8">
                                            <div className="text-center">
                                                <div className="relative mb-8">
                                                    <div className="absolute inset-0 opacity-50 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur-2xl animate-pulse"></div>
                                                    <div className="relative flex items-center justify-center mx-auto transition-all duration-700 w-28 h-28 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl transform-gpu group-hover:rotate-12">
                                                        <Fingerprint className="w-16 h-16 text-white transition-transform duration-700 group-hover:scale-110" />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4 mb-8">
                                                    <div className={`p-4 border ${bgGlass} ${borderColor} rounded-2xl backdrop-blur-sm`}>
                                                        <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text">20,000+</div>
                                                        <div className={`text-xs ${textMuted}`}>Monthly scans</div>
                                                    </div>
                                                    <div className={`p-4 border ${bgGlass} ${borderColor} rounded-2xl backdrop-blur-sm`}>
                                                        <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text">99.9%</div>
                                                        <div className={`text-xs ${textMuted}`}>Accuracy rate</div>
                                                    </div>
                                                </div>

                                                <div className={`inline-flex items-center gap-3 px-6 py-3 border rounded-full ${bgGlass} ${borderColor} backdrop-blur-sm`}>
                                                    <span className="relative flex w-3 h-3">
                                                        <span className="absolute inline-flex w-full h-full bg-green-400 rounded-full opacity-75 animate-ping"></span>
                                                        <span className="relative inline-flex w-3 h-3 bg-green-500 rounded-full"></span>
                                                    </span>
                                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>400+ employees online now</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES SECTION */}
            <section className={`py-24 px-6 ${bgGradient} relative transition-colors duration-300`}>
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-blue-600/5 via-transparent to-transparent' : 'bg-gradient-to-b from-blue-400/5 via-transparent to-transparent'}`}></div>

                <div className="container relative z-10 mx-auto">
                    <div className="mb-16 text-center">
                        <span className={`px-4 py-2 text-sm font-semibold tracking-wider uppercase rounded-full ${isDark ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' : 'text-blue-700 bg-blue-100 border-blue-300'} border`}>
                            Everything you need
                        </span>
                        <h2 className={`mt-6 text-4xl font-bold md:text-5xl ${isDark ? 'text-transparent bg-gradient-to-r from-white to-blue-100 bg-clip-text' : 'text-gray-900'}`}>
                            Complete Management
                        </h2>
                        <p className={`max-w-3xl mx-auto mt-4 text-xl ${textSecondary}`}>
                            Powerful features that work together to eliminate manual sheets and proxy.
                        </p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {functionalities.map((item, index) => {
                            const Icon = item.icon;

                            return (
                                <div
                                    key={index}
                                    className={`relative p-6 transition-all duration-500 border group ${isDark ? 'bg-gradient-to-br from-white/5 to-white/0' : 'bg-white'} rounded-2xl ${borderColor} ${borderHover} backdrop-blur-sm hover:shadow-2xl ${isDark ? 'hover:shadow-blue-600/10' : 'hover:shadow-blue-400/20'} hover:-translate-y-2`}
                                >
                                    <div className={`absolute inset-0 transition-all duration-500 bg-gradient-to-br from-blue-600/0 to-cyan-600/0 group-hover:from-blue-600/10 group-hover:to-cyan-600/10 rounded-2xl`}></div>

                                    <div className="relative">
                                        <div className={`flex items-center justify-center mb-4 transition-all duration-500 w-14 h-14 ${isDark ? 'bg-gradient-to-br from-blue-600/20 to-cyan-600/20 group-hover:from-blue-600/30 group-hover:to-cyan-600/30' : 'bg-gradient-to-br from-blue-100 to-cyan-100 group-hover:from-blue-200 group-hover:to-cyan-200'} rounded-xl`}>
                                            <Icon className={`transition-all duration-500 w-7 h-7 ${isDark ? 'text-blue-400 group-hover:text-cyan-400' : 'text-blue-600 group-hover:text-cyan-600'} group-hover:scale-110`} />
                                        </div>

                                        <h3 className={`mb-2 text-xl font-bold transition-all duration-500 ${isDark ? 'text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-cyan-400 group-hover:bg-clip-text' : 'text-gray-900 group-hover:text-blue-600'}`}>
                                            {item.title}
                                        </h3>

                                        <p className={`text-sm leading-relaxed ${textSecondary}`}>
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="grid gap-6 mt-24 md:grid-cols-4">
                        {[
                            { value: "99.9%", label: "Accuracy rate", icon: "fa-check-circle" },
                            { value: "8h+", label: "Weekly Time saved", icon: "fa-clock" },
                            { value: "20+", label: "Organizations trust us", icon: "fa-building" },
                            { value: "25+", label: "Integrations", icon: "fa-plug" }
                        ].map((stat, index) => (
                            <div key={index} className="relative group">
                                <div className={`absolute inset-0 transition-opacity duration-500 opacity-0 ${isDark ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20' : 'bg-gradient-to-r from-blue-400/20 to-cyan-400/20'} rounded-2xl blur-xl group-hover:opacity-100`}></div>
                                <div className={`relative p-6 text-center border ${isDark ? 'bg-gradient-to-br from-white/5 to-white/0' : 'bg-white'} rounded-2xl ${borderColor} backdrop-blur-sm`}>
                                    <i className={`fas ${stat.icon} text-2xl ${isDark ? 'text-blue-400' : 'text-blue-600'} mb-3`}></i>
                                    <div className="text-4xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text">
                                        {stat.value}
                                    </div>
                                    <div className={`mt-2 text-sm ${isDark ? 'text-blue-200/80' : 'text-gray-600'}`}>{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* FINAL CTA */}
            <footer className={`relative ${isDark ? 'bg-gradient-to-b from-[#070A12] to-[#0B0F1C]' : 'bg-gradient-to-b from-gray-100 to-gray-50'} pt-24 pb-12 border-t ${borderColor}`}>

                <div className="container relative z-10 px-6 mx-auto">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 mb-8 shadow-2xl rounded-3xl bg-gradient-to-br from-blue-500 to-cyan-500">
                            <Fingerprint className="w-8 h-8 text-white" />
                        </div>

                        <h2 className={`text-4xl font-bold md:text-5xl ${isDark ? 'text-transparent bg-gradient-to-r from-white to-blue-100 bg-clip-text' : 'text-gray-900'}`}>
                            Ready to Transform?
                        </h2>
                        <p className={`mt-4 text-xl ${textSecondary}`}>
                            Join 20+ Organizations that have eliminated manual sheets and proxy.
                        </p>

                        <div className="flex flex-wrap justify-center gap-4 mt-10">
                            <button onClick={() => { handlerClick('Product') }} className="relative px-8 py-4 font-semibold text-white transition-all duration-300 transform group bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl hover:shadow-2xl hover:shadow-blue-600/30 hover:scale-105">
                                <span className="relative z-10 flex items-center gap-2">
                                    <i className="fas fa-user-plus"></i>
                                    Buy Now
                                </span>
                                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl blur-xl group-hover:opacity-50"></div>
                            </button>
                            <button onClick={() => { handlerClick('Contact') }} className={`flex items-center gap-2 px-8 py-4 font-semibold transition-all duration-300 border ${bgGlass} ${bgGlassHover} ${borderColor} rounded-xl backdrop-blur-sm hover:border-white/20`}>
                                <i className={`${isDark ? 'text-blue-400' : 'text-blue-600'} fas fa-phone-alt`}></i>
                                Contact Sales
                            </button>
                        </div>

                        <div className={`flex items-center justify-center gap-6 mt-8 ${textMuted}`}>
                            <i className={`transition-colors cursor-pointer fab fa-twitter ${isDark ? 'hover:text-blue-400' : 'hover:text-blue-600'}`}></i>
                            <i className={`transition-colors cursor-pointer fab fa-linkedin ${isDark ? 'hover:text-blue-400' : 'hover:text-blue-600'}`}></i>
                            <i className={`transition-colors cursor-pointer fab fa-github ${isDark ? 'hover:text-blue-400' : 'hover:text-blue-600'}`}></i>
                            <i className={`transition-colors cursor-pointer fab fa-facebook ${isDark ? 'hover:text-blue-400' : 'hover:text-blue-600'}`}></i>
                        </div>
                    </div>
                </div>
            </footer>

            <Footer />

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; }
                    50% { opacity: 0.2; }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2000ms;
                }
                
                @keyframes slide-down {
                    0% {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-down {
                    animation: slide-down 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default HomePage;
